import React, { useState } from 'react'
import { Button } from './ui/Button'
import { Badge } from './ui/Badge'
import { LoadingSpinner } from './ui/LoadingSpinner'
import { RequirementModal } from './RequirementModal'
import { 
  Edit, 
  Trash2, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Plus,
  Eye,
  EyeOff,
  Tag,
  FileText
} from 'lucide-react'

interface Requirement {
  requirement_id: string
  description: string
  category: string
  title: string
  priority: 'high' | 'medium' | 'low'
  section?: string
  page_number?: number
  weight?: number
  is_mandatory: boolean
  // extraction_confidence?: number
  extracted_text?: string
}

interface Project {
  id: string
  title: string
  status: string
  extraction_status: 'pending' | 'in_progress' | 'completed' | 'failed'
  requirements: Requirement[]
}

interface RequirementsTabProps {
  project: Project
  onUpdateRequirement?: (requirement: Requirement) => void
  onDeleteRequirement?: (requirementId: string) => void
  onAddRequirement?: (requirementData: Partial<Requirement>) => void
}

export const RequirementsTab: React.FC<RequirementsTabProps> = ({
  project,
  onUpdateRequirement,
  onDeleteRequirement,
  onAddRequirement
}) => {
  const [showExtractedText, setShowExtractedText] = useState<{[key: string]: boolean}>({})
  const [editingRequirement, setEditingRequirement] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add')
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null)

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getCategoryColor = (category: string | string[] | undefined) => {
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-teal-100 text-teal-800 border-teal-200'
    ]
    
    // Handle different category types
    const categoryStr = Array.isArray(category) 
      ? category.join(' ') 
      : (category || 'general')
    
    // Simple hash to get consistent color for category
    const hash = categoryStr.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }

  // const getConfidenceIcon = (confidence?: string) => {
  //   switch (confidence) {
  //     case 'high': return <CheckCircle className="w-4 h-4 text-green-500" />
  //     case 'medium': return <AlertTriangle className="w-4 h-4 text-yellow-500" />
  //     case 'low': return <XCircle className="w-4 h-4 text-red-500" />
  //     default: return <AlertTriangle className="w-4 h-4 text-gray-500" />
  //   }
  // }

  // const getConfidenceColor = (confidence?: string) => {
  //   switch (confidence) {
  //     case 'high': return 'text-green-700 bg-green-50'
  //     case 'medium': return 'text-yellow-700 bg-yellow-50'
  //     case 'low': return 'text-red-700 bg-red-50'
  //     default: return 'text-gray-700 bg-gray-50'
  //   }
  // }

  const toggleExtractedText = (requirementId: string) => {
    setShowExtractedText(prev => ({
      ...prev,
      [requirementId]: !prev[requirementId]
    }))
  }

  const handleAddClick = () => {
    setModalMode('add')
    setSelectedRequirement(null)
    setShowModal(true)
  }

  const handleEditClick = (requirement: Requirement) => {
    setModalMode('edit')
    setSelectedRequirement(requirement)
    setShowModal(true)
  }

  const handleModalSubmit = (requirementData: Partial<Requirement>) => {
    if (modalMode === 'add' && onAddRequirement) {
      onAddRequirement(requirementData)
    } else if (modalMode === 'edit' && onUpdateRequirement && selectedRequirement) {
      onUpdateRequirement({
        ...selectedRequirement,
        ...requirementData
      })
    }
    setShowModal(false)
  }

  const mandatoryCount = project.requirements?.filter(req => req.is_mandatory).length || 0
  const optionalCount = (project.requirements?.length || 0) - mandatoryCount

  // Show different content based on extraction status
  if (project.extraction_status === 'in_progress') {
    return (
      <div className="space-y-6">
        {/* Header with loading state */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Analyzing RFP Document</h2>
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-600 font-medium">Processing...</span>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            We're extracting requirements from your RFP document. This usually takes 1-3 minutes depending on document size.
          </p>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">--</div>
              <div className="text-sm text-gray-600">Total Requirements</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">--</div>
              <div className="text-sm text-gray-600">Mandatory</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">--</div>
              <div className="text-sm text-gray-600">Optional</div>
            </div>
          </div>
        </div>

        {/* Skeleton loading for requirements table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requirement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th> */}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  if (project.extraction_status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <XCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Analysis Failed</h3>
        <p className="text-gray-600 text-center max-w-md mb-4">
          We encountered an error while analyzing your RFP document. Please try again or contact support.
        </p>
        <Button onClick={() => window.location.reload()}>Retry Analysis</Button>
      </div>
    )
  }

  if (project.extraction_status === 'completed' && (!project.requirements || project.requirements.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <FileText className="w-12 h-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Requirements Found</h3>
        <p className="text-gray-600 text-center max-w-md mb-4">
          No requirements were found in the RFP document. You can manually add requirements using the button below.
        </p>
        {onAddRequirement && (
          <Button onClick={handleAddClick}>
            <Plus className="w-4 h-4 mr-2" />
            Add Requirement
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Requirements Overview</h2>
          {onAddRequirement && (
            <Button onClick={handleAddClick} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Requirement
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{project.requirements?.length || 0}</div>
            <div className="text-sm text-gray-600">Total Requirements</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{mandatoryCount}</div>
            <div className="text-sm text-gray-600">Mandatory</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{optionalCount}</div>
            <div className="text-sm text-gray-600">Optional</div>
          </div>
        </div>
      </div>

      {/* Requirements Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requirement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confidence
                </th> */}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {project.requirements?.map((requirement, index) => (
                <React.Fragment key={requirement.requirement_id || index}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-500">#{index + 1}</span>
                            {requirement.is_mandatory && (
                              <Badge variant="destructive" className="text-xs">Mandatory</Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-900 leading-relaxed">
                            {requirement.title}
                          </p>
                          {requirement.page_number && (
                            <p className="text-xs text-gray-500 mt-1">Page {requirement.page_number}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getCategoryColor(requirement.category)}`}
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {Array.isArray(requirement.category) 
                          ? requirement.category.join(', ') 
                          : (requirement.category || 'General')
                        }
                      </Badge>
                    </td>
                    
                    <td className="px-6 py-4">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getPriorityColor(requirement.priority)}`}
                      >
                        {requirement.priority.toUpperCase()}
                      </Badge>
                    </td>
                    
                    {/* <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getConfidenceIcon(requirement.extraction_confidence)}
                        <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(requirement.extraction_confidence)}`}>
                          {requirement.extraction_confidence?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </div>
                    </td> */}
                    
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {requirement.description && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExtractedText(requirement.requirement_id)}
                            className="p-1"
                            title="Toggle description"
                          >
                            {showExtractedText[requirement.requirement_id] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        
                        {onUpdateRequirement && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(requirement)}
                            className="p-1"
                            title="Edit requirement"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {onDeleteRequirement && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteRequirement(requirement.requirement_id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Delete requirement"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  
                  {/* Extracted text row */}
                  {showExtractedText[requirement.requirement_id] && requirement.description && (
                    <tr>
                      <td colSpan={6} className="px-6 py-3 bg-gray-50">
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Description:</span>
                          <div className="mt-1 p-3 bg-white rounded border text-gray-700 text-xs">
                            "{requirement.description}"
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Requirement Modal */}
      <RequirementModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        requirement={selectedRequirement}
        mode={modalMode}
      />
    </div>
  )
}